import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, ScrollText } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const actionColor = (a) => a === "delete" ? "bg-red-100 text-red-700 border-red-200" : a === "write" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200";
const fmtTime = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/audit-logs").then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query) return logs;
    const q = query.toLowerCase();
    return logs.filter((l) => ["user_name", "role", "module", "action", "path", "method"].some((k) => String(l[k] || "").toLowerCase().includes(q)));
  }, [logs, query]);

  return (
    <div data-testid="module-audit">
      <div className="mb-5 flex items-center gap-2"><ScrollText className="w-6 h-6 text-[#0B132B]" /><div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-0.5">Admin only · who changed what, and when</p></div></div>

      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-sm text-slate-500">{filtered.length} entr{filtered.length !== 1 ? "ies" : "y"}</p>
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="search-audit" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-full sm:w-64 bg-white" /></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            {["Date / Time", "User", "Role", "Module", "Action", "Endpoint"].map((h, i) => (
              <th key={i} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No audit entries yet</td></tr>
              : filtered.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtTime(l.at)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{l.user_name}</td>
                  <td className="px-4 py-3 text-slate-600">{l.role}</td>
                  <td className="px-4 py-3 text-slate-700 capitalize">{l.module}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-sm capitalize ${actionColor(l.action)}`}>{l.action}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{l.method} {l.path}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
